import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { FileCheck, Calendar, DollarSign, User, BookOpen, Mail, Phone, MapPin, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminEnrollments = () => {
  const { token } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);

  const fetchEnrollments = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/enrollments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEnrollments(response.data);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    fetchEnrollments();
  }, [fetchEnrollments]);

  const handleRegister = async (enrollmentId) => {
    setActionLoading(enrollmentId);
    try {
      const response = await axios.post(
        `${API_URL}/api/enrollments/${enrollmentId}/register`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Student registered successfully! Account created with password = phone number. Email: ${response.data.student_email}`);
      await fetchEnrollments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register student');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (enrollmentId) => {
    setActionLoading(enrollmentId);
    try {
      await axios.put(
        `${API_URL}/api/enrollments/${enrollmentId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Enrollment rejected');
      await fetchEnrollments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject enrollment');
    } finally {
      setActionLoading(null);
    }
  };

  const totalRevenue = enrollments.reduce((sum, e) => sum + (e.fee_paid || 0), 0);
  const activeCount = enrollments.filter(e => e.status === 'active').length;
  const pendingCount = enrollments.filter(e => e.status === 'pending').length;

  const filteredEnrollments = filterStatus === 'all'
    ? enrollments
    : enrollments.filter(e => e.status === filterStatus);

  return (
    <AdminLayout title="Enrollments">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className={`cursor-pointer ${filterStatus === 'all' ? 'ring-2 ring-primary' : ''}`} onClick={() => setFilterStatus('all')}>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{enrollments.length}</p>
            <p className="text-sm text-muted-foreground">Total Enrollments</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">₹{totalRevenue.toLocaleString()}</p>
            <p className="text-sm text-muted-foreground">Total Revenue</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer ${filterStatus === 'active' ? 'ring-2 ring-green-400' : ''}`} onClick={() => setFilterStatus(filterStatus === 'active' ? 'all' : 'active')}>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{activeCount}</p>
            <p className="text-sm text-muted-foreground">Active</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer ${filterStatus === 'pending' ? 'ring-2 ring-yellow-400' : ''}`} onClick={() => setFilterStatus(filterStatus === 'pending' ? 'all' : 'pending')}>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-yellow-600">{pendingCount}</p>
            <p className="text-sm text-muted-foreground">Pending</p>
          </CardContent>
        </Card>
      </div>

      {filterStatus !== 'all' && (
        <div className="mb-4 flex items-center gap-2">
          <Badge variant="outline">Filtering: {filterStatus}</Badge>
          <Button variant="ghost" size="sm" onClick={() => setFilterStatus('all')}>Clear filter</Button>
        </div>
      )}

      {loading ? (
        <div className="space-y-4">{[1, 2, 3].map(i => <Card key={i} className="animate-pulse"><CardContent className="h-20"></CardContent></Card>)}</div>
      ) : filteredEnrollments.length > 0 ? (
        <div className="space-y-4">
          {filteredEnrollments.map((enrollment) => {
            const isPending = enrollment.status === 'pending';
            const displayName = isPending
              ? enrollment.lead_name
              : (enrollment.student?.name || `Student ${enrollment.student_id?.slice(-8)}`);
            const displayEmail = isPending ? enrollment.lead_email : enrollment.student?.email;
            const displayPhone = isPending ? enrollment.lead_phone : enrollment.student?.phone;

            return (
              <Card key={enrollment.id} data-testid={`enrollment-${enrollment.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <FileCheck className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      {/* Name + Status */}
                      <div className="flex items-center gap-2 mb-1">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <h4 className="font-semibold">{displayName}</h4>
                        <Badge className={
                          enrollment.status === 'active' ? 'bg-green-100 text-green-700' :
                            enrollment.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                              ''
                        }>
                          {isPending ? 'Pending Lead' : enrollment.status}
                        </Badge>
                      </div>

                      {/* Contact details */}
                      {(displayEmail || displayPhone) && (
                        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-2">
                          {displayEmail && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3.5 h-3.5" /> {displayEmail}
                            </span>
                          )}
                          {displayPhone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3.5 h-3.5" /> {displayPhone}
                            </span>
                          )}
                          {isPending && enrollment.lead_address && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3.5 h-3.5" /> {enrollment.lead_address}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Course + Payment details */}
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        {enrollment.course && (
                          <span className="flex items-center gap-1">
                            <BookOpen className="w-3.5 h-3.5" />
                            {enrollment.course.name}
                            {enrollment.course.category && <Badge variant="outline" className="ml-1 text-xs py-0">{enrollment.course.category}</Badge>}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {enrollment.enrollment_date ? new Date(enrollment.enrollment_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}
                        </span>
                        {!isPending && (
                          <span className="flex items-center gap-1">
                            <DollarSign className="w-3.5 h-3.5" />
                            ₹{enrollment.fee_paid?.toLocaleString()}
                          </span>
                        )}
                        {enrollment.discount_applied > 0 && (
                          <Badge variant="secondary">₹{enrollment.discount_applied.toLocaleString()} discount</Badge>
                        )}
                      </div>

                      {/* Action buttons for pending */}
                      {isPending && (
                        <div className="flex gap-2 mt-3">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleRegister(enrollment.id)}
                            disabled={actionLoading === enrollment.id}
                          >
                            {actionLoading === enrollment.id ? 'Processing...' : 'Register Student'}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleReject(enrollment.id)}
                            disabled={actionLoading === enrollment.id}
                          >
                            Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card><CardContent className="py-16 text-center"><FileCheck className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" /><h3 className="font-semibold mb-2">No Enrollments Yet</h3><p className="text-sm text-muted-foreground">{filterStatus !== 'all' ? 'No enrollments with this status.' : 'No enrollments yet.'}</p></CardContent></Card>
      )}
    </AdminLayout>
  );
};

export default AdminEnrollments;
